import {
  Query,
  ReferralDetailResultTypenames,
} from '@varo/graphql-schema-types';
import backgroundSource from 'assets/images/vxl/component/productIntroScreen/background-texture-lavender.png';
import { setStringAsync } from 'expo-clipboard';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import ReferralLimitReached from 'features/rewards/components/ReferralLimit/ReferralLimitReached';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share, StyleSheet, View } from 'react-native';
import FetchReferralDetail from 'services/graphql/queries/FetchReferralDetails';
import PdfViewer from 'shared/components/PdfRenderer/PdfViewer';
import { IconAvatarProps } from 'shared/components/vxl/avatar/IconAvatar/IconAvatar';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import { useAutoHideSnackbar } from 'shared/hooks/snackBar/useAutoHideSnackbar';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ProductIntroScreen from 'shared/patterns/vxl/ProductIntroScreen/ProductIntroScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const SNACKBAR_DURATION = 4000;

const InviteFriendsScreen = () => {
  const router = useRouter();
  const [pdfUrl, setPdfUrl] = useState('');
  const [showSnackBar, setShowSnackBar] = useState(false);
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'manageAccount',
  });

  const [{ data, error, fetching: loading }, refetchReferralDetail] =
    useQuery<Query>({
      query: FetchReferralDetail,
      requestPolicy: 'cache-and-network',
    });
  const refresh = () => {
    refetchReferralDetail({ requestPolicy: 'network-only' });
  };
  const cancel = () => {
    router.back();
  };

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const responseError = useFormattedCombinedError(error);

  const referralDetail = data?.referralDetail;
  const referralDetailError =
    referralDetail?.__typename ===
      ReferralDetailResultTypenames.ReferralDetailError ||
    referralDetail?.__typename ===
      ReferralDetailResultTypenames.ReferralDetailExpired
      ? referralDetail
      : undefined;

  const inviteFriendsDetails =
    referralDetail?.__typename ===
    ReferralDetailResultTypenames.ReferralCardDetail
      ? referralDetail
      : undefined;

  const isLimitReached = inviteFriendsDetails?.offerCode === null;

  const screenAnalytics =
    !loading &&
    !error &&
    referralDetail?.__typename ===
      ReferralDetailResultTypenames.ReferralCardDetail
      ? { key: isLimitReached ? 'referralLimitMet' : 'referralInvite' }
      : null;

  useViewDidFocusScreenAnalyticsEvent(screenAnalytics);

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: refresh,
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel =
    error || referralDetailError
      ? {
          screenType: ErrorScreenType.Error,
          messageHeadline:
            referralDetailError?.title ?? responseError?.title ?? defaultTitle,
          messageBody:
            referralDetailError?.message ??
            responseError?.message ??
            defaultMessage,
          ...buttons,
        }
      : null;

  const logShareLinkClickedAnalytics = usePressableAnalytics({
    analyticsId: 'shareLinkClicked',
  });

  const logReferralTermsClickedAnalytics = usePressableAnalytics({
    analyticsId: 'referralTermsClicked',
  });

  const logTermsClickedAnalytics = usePressableAnalytics({
    analyticsId: 'termsClicked',
    customAttributes: {
      button_name: 'see_full_terms',
    },
  });

  //Copy to clipboard functionality
  const copyToClipBoard = async (text: string) => {
    await setStringAsync(text);
    setShowSnackBar(true);
  };

  //Opens Applications tray which supports sharing the content
  const shareLinkPress = async () => {
    logShareLinkClickedAnalytics();
    await Share.share({
      message: inviteFriendsDetails?.shareLinkText ?? '',
    });
  };

  const handleBackArrowNavigation = () => {
    if (pdfUrl) {
      setPdfUrl('');
    } else {
      navigateBack();
    }
  };

  useAutoHideSnackbar(
    Boolean(showSnackBar),
    () => {
      setShowSnackBar(false);
    },
    SNACKBAR_DURATION,
  );

  const mainContent = isLimitReached ? (
    <ReferralLimitReached
      headlineText={inviteFriendsDetails.cardTitle?.toUpperCase()}
      limitMessage={inviteFriendsDetails.subTitle ?? undefined}
      legalLinkText={inviteFriendsDetails.legalLink?.text ?? undefined}
      onTermsPress={() => {
        logTermsClickedAnalytics();
        if (inviteFriendsDetails.legalLink?.url) {
          setPdfUrl(inviteFriendsDetails.legalLink.url);
        }
      }}
    />
  ) : (
    <ProductIntroScreen
      primaryButton={{
        label: inviteFriendsDetails?.cta?.title ?? '',
        onPress: shareLinkPress,
      }}
      headlineText={inviteFriendsDetails?.cardTitle?.toUpperCase() ?? ''}
      headlineTextStyle="expressive"
      bodyContent={
        <View style={[styles.container, styles.bodyContentContainer]}>
          {inviteFriendsDetails?.partnerOfferCardDetailTerms?.map((item) => (
            <View key={item?.id}>
              <ListItem
                primaryCopy={item?.heading ?? ''}
                secondaryCopy={item?.subHeading ?? ''}
                leadingAccessory={{
                  id: item?.id,
                  name: VXLComponentId.ICON_AVATAR,
                  props: {
                    variant: 'SM',
                    iconKey: item?.icon,
                  } as IconAvatarProps,
                }}
                primaryNumberOfLines={3}
                showsChevron={false}
              />
              <View style={styles.marginHorizontal}>
                <HairLineDivider />
              </View>
            </View>
          ))}
          <View style={styles.padding}>
            <HyperlinkSmText
              numberOfLines={0}
              onPress={() => {
                logReferralTermsClickedAnalytics();
                if (inviteFriendsDetails?.legalLink?.url) {
                  setPdfUrl(inviteFriendsDetails.legalLink.url);
                }
              }}
            >
              {inviteFriendsDetails?.legalLink?.text}
            </HyperlinkSmText>
          </View>
        </View>
      }
      backgroundTexture={backgroundSource}
      visual="HIGH_FIVE_A"
      topPadding={80}
      stickyItem={
        inviteFriendsDetails?.displayUrl || inviteFriendsDetails?.offerUrl ? (
          <View style={[styles.stickyItem, styles.copyContainer]}>
            <BodyBaseText numberOfLines={1} style={styles.copyText}>
              {inviteFriendsDetails.displayUrl ?? inviteFriendsDetails.offerUrl}
            </BodyBaseText>
            <View style={styles.copyIcon}>
              <IconButton
                iconButtonAccessibilityLabel="COPY"
                iconSize="MD"
                iconColor={theme.color.text.primary}
                iconKey="COPY"
                analyticsId="linkCopyClicked"
                iconOnPress={() =>
                  copyToClipBoard(inviteFriendsDetails.offerUrl)
                }
              />
            </View>
          </View>
        ) : null
      }
    />
  );

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          headerTransparent: !pdfUrl,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              {...props}
              transparent={!pdfUrl}
              primaryOnPress={handleBackArrowNavigation}
            />
          ),
        }}
      />
      {!pdfUrl ? (
        mainContent
      ) : (
        <PdfViewer url={pdfUrl} onOpen={() => setPdfUrl('')} />
      )}
      <ScreenTransitionOverlay
        isLoading={loading}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
        LoadingComponent={<LoadingSpinner />}
      />
      {showSnackBar ? <Snackbar text={translatedText('copiedText')} /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  bodyContentContainer: { paddingBottom: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
    flex: 1,
  },
  copyContainer: {
    flexDirection: 'row',
    padding: theme.spacing.m,
  },
  copyIcon: {
    justifyContent: 'flex-end',
    marginLeft: theme.spacing.s,
  },
  copyText: { flex: 1 },
  marginHorizontal: { marginHorizontal: theme.spacing.m },
  padding: { padding: theme.spacing.m },
  stickyItem: {
    borderColor: theme.color.border.primarySolid,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
  },
});

export default InviteFriendsScreen;
